import { useEffect, useMemo, useState } from "react";
import useFetch from "use-http";
import PageQuery from "common/types/PageQuery";
import { compareByMatchStart, matchPosition } from "utils/switcherSearch";

const SEARCH_DEBOUNCE_MS = 250;

const nameCollator = new Intl.Collator("de", { numeric: true });

/**
 * True when a list context's current query is its plain, unfiltered list, so
 * that once every page is loaded the browser holds ALL entries. The project
 * list shares its query with the projects page, which can have a search or
 * filters applied; a fully loaded filtered list is not "everything".
 */
export const isUnfilteredQuery = (query: PageQuery | undefined) => {
	if (!query) {
		return false;
	}
	const filters = Object.values(query.filters ?? {});
	const hasFilter = filters.some(
		(value) =>
			value !== undefined &&
			value !== null &&
			value !== "" &&
			!(Array.isArray(value) && value.length === 0),
	);
	return !query.search?.text && !hasFilter;
};

interface SwitcherSearchOptions<T> {
	/** Request path for the trimmed text, or null to search locally only. Keep
	 *  it stable (useCallback): a new function restarts the search. */
	buildUrl: (text: string) => string | null;
	/** The label the switcher shows for an entry; it is matched and sorted by. */
	getLabel: (item: T) => string;
	/** Further fields the text is matched against, as dotted paths; the same
	 *  list the server searches (search[fields]), so both find the same entries. */
	searchFields?: string[];
	getKey: (item: T) => string;
	/** The entries the switcher has loaded so far. */
	loadedItems: T[];
	/** True when loadedItems is every entry there is; the server is then not asked. */
	loadedComplete: boolean;
}

/**
 * Search for a switcher whose list is paged (projects, scenes).
 *
 * The switcher only holds the pages loaded so far, so filtering those alone
 * would report "not found" for anything further down. The typed text also
 * goes to the list endpoint as search[text] on meta.name (a case-insensitive
 * substring match, see hegiasGo's pagination middleware), so an entry that
 * does not show up really does not exist.
 *
 * The loaded entries are filtered at once, so what is already on screen stays
 * there while the server answers. The server's hits are then added to them,
 * never swapped in: an entry shown once does not vanish, including one the
 * server misses because its regex does not fold accents ("ar" → "Ärzte").
 * Hits are ordered by name, those starting with the text first, so the list
 * does not jump when the server's answer arrives.
 *
 * `results` is null while nothing is typed, so the switcher shows its normal
 * list. `loading` is true while the server's answer for the current text is
 * still outstanding.
 */
const valueAt = (item: unknown, path: string): unknown =>
	path
		.split(".")
		.reduce<unknown>(
			(value, key) =>
				value && typeof value === "object"
					? (value as Record<string, unknown>)[key]
					: undefined,
			item,
		);

const useSwitcherSearch = <T>({
	buildUrl,
	getLabel,
	searchFields = [],
	getKey,
	loadedItems,
	loadedComplete,
}: SwitcherSearchOptions<T>) => {
	const [searchText, setSearchText] = useState("");
	const [serverHits, setServerHits] = useState<{ text: string; items: T[] }>();
	const { get, response } = useFetch();

	const text = searchText.trim();
	const url = text && !loadedComplete ? buildUrl(text) : null;

	useEffect(() => {
		if (!url) {
			return undefined;
		}

		let stale = false;
		const timer = window.setTimeout(async () => {
			const data = await get(url);
			if (!stale) {
				setServerHits({
					text,
					items: response.ok ? data?.results ?? [] : [],
				});
			}
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			stale = true;
			window.clearTimeout(timer);
		};
		// `text` is part of `url`; get/response are not part of what is searched.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [url]);

	const serverAnswered = serverHits?.text === text;

	const results = useMemo(() => {
		if (!text) {
			return null;
		}
		const byKey = new Map<string, T>();
		const matches = (item: T) =>
			[getLabel(item), ...searchFields.map((path) => valueAt(item, path))]
				.filter((value): value is string => typeof value === "string")
				.some((value) => matchPosition(value, text) >= 0);
		loadedItems
			.filter(matches)
			.forEach((item) => byKey.set(getKey(item), item));
		if (url && serverAnswered) {
			serverHits.items.forEach((item) => {
				if (!byKey.has(getKey(item))) {
					byKey.set(getKey(item), item);
				}
			});
		}
		return [...byKey.values()]
			.sort((a, b) => nameCollator.compare(getLabel(a), getLabel(b)))
			.sort((a, b) => compareByMatchStart(getLabel(a), getLabel(b), text));
		// getLabel/getKey/searchFields are configuration, not inputs.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [text, loadedItems, url, serverAnswered, serverHits]);

	return {
		searchText,
		setSearchText,
		reset: () => setSearchText(""),
		isSearching: text !== "",
		results,
		loading: Boolean(url) && !serverAnswered,
	};
};

export default useSwitcherSearch;
